<template>
    <div class="row m-0">
        <page-title :title="$t('login')" :showTitle="false"></page-title>
        <div class="col-sm-8 col-md-6 col-lg-4 mx-auto mt-5">
            <div class="loader" v-if="loading"></div>
            <div v-if="!loading" class="card">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('login') }}</div>
                </div>
                <div class="card-body">
                    <div>
                        <div class="form-group">
                            <label for="email" class="required">{{ $t('email') }}</label>
                            <input
                                id="email"
                                type="email"
                                class="form-control"
                                name="email"
                                :data-vv-as="$t('email')"
                                v-model="email"
                                v-validate="'required|email'"
                                @keyup.enter="login"
                            />
                            <span class="text-danger">{{ errors.first('email') }}</span>
                        </div>
                        <div class="form-group">
                            <label for="password" class="required">{{ $t('password') }}</label>
                            <input
                                id="password"
                                type="password"
                                class="form-control"
                                name="password"
                                :data-vv-as="$t('password')"
                                v-model="password"
                                v-validate="'required'"
                                @keyup.enter="login"
                            />
                            <span class="text-danger">{{ errors.first('password') }}</span>
                        </div>
                        <div>
                            <!-- <button class="btn btn-primary w-100 ml-0" @click="login" :disabled="loading">{{ $t('login') }}</button> -->
                            <div class="text-center">
                                <vs-button class="mb-3 w-25" color="#009688" type="filled" @click="login"
                                           :disabled="loading">{{ $t('login').toUpperCase() }}
                                </vs-button>
                            </div>
                            <div class="divider">
                                <div class="spacer"></div>
                                <div class="text mx-2">или</div>
                                <div class="spacer"></div>
                            </div>
                            <div class="text-center">
                                <a :href="certLoginUrl" class="btn btn-primary mx-0">{{$t('clientCertLogin')}}</a>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <router-link :to="registerPath" class :disabled="loading">{{ $t('register') }}</router-link>
                            </div>
                            <div class="col-md-6 mb-2">
                                <a href="#" class="float-md-right" @click="showForgotPassword=true">{{$t('forgottenPassword')}}?</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <vs-prompt
            color="success"
            @accept="sendResetPasswordMail"
            @cancel="closeModal"
            @close="closeModal"
            :active.sync="showForgotPassword"
            :is-valid="validEmail"
            :title="$t('forgottenPassword')"
            :accept-text="$t('buttons.submit.btnText')"
            :cancel-text="$t('buttons.cancel')">
            <div class="con-exemple-prompt">
                <span>{{$t('forgotPasswordEmailHelpMessage')}}</span>
                <vs-input style="width:100%" :placeholder="$t('email')" v-model="forgotPasswordEmail" />
                <vs-alert :active="!validEmail" color="danger">{{$t('invalidEmail')}}</vs-alert>
            </div>
        </vs-prompt>
    </div>
</template>

<script>
    import authService from '@/services/auth.service';
    import { HttpStatusCode } from '@/models/enums';
    import PageTitle from '@/components/common/PageTitle';

    export default {
        components: {
            PageTitle
        },
        data: function () {
            return {
                email: '',
                password: '',
                loading: false,
                showForgotPassword: false,
                forgotPasswordEmail: '',
                registerPath: '/register'
            };
        },
        computed: {
            certLoginUrl() {
                return this.$store.getters.baseUrl + '/certificate/login';
            },
            validEmail() {
                const regExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return regExp.test(String(this.forgotPasswordEmail).toLowerCase());
            }
        },
        methods: {
            async login() {
                const validationResult = await this.$validator.validateAll();
                if (validationResult) {
                    this.loading = true;
                    try {
                        await authService.login({
                            email: this.email,
                            password: this.password
                        });

                        const { returnUrl } = this.$route.query;

                        this.$store.commit('authenticateUser', JSON.parse(localStorage.getItem('user')));
                        this.$store.commit('updateUserRoles', JSON.parse(localStorage.getItem('user')).roles);
                        this.$store.commit('updateClaims', JSON.parse(localStorage.getItem('user')).userClaims);

                        await this.$store.dispatch('loadUserPolicies');
                        await this.$store.dispatch('loadUserSettingsAsync');

                        this.$router.push({ path: returnUrl || '/' });
                    } catch (error) {
                        this.loading = false;
                        if (error.response.status === HttpStatusCode.BadRequest) {
                            this.$vs.notify({
                                title: this.$t('login'),
                                text: this.$t('notifications.invalidUserCredentials'),
                                position: 'top-center',
                                color: 'danger'
                            });
                        } else {
                            this.$vs.notify({
                                title: this.$t('login'),
                                text: this.$helpers.errorText(error),
                                position: 'top-center',
                                color: 'danger'
                            });
                        }
                    }
                } else {
                    this.$vs.notify({
                        title: this.$t('login'),
                        text: this.$t('notifications.fieldValidationErrors'),
                        position: 'top-center',
                        color: 'danger'
                    });
                }
            },
            sendResetPasswordMail() {
                this.loading = true;
                authService
                    .sendResetPasswordMail({
                        email: this.forgotPasswordEmail
                    })
                    .then(data => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('forgottenPasswordOk'),
                            position: 'top-center',
                            color: 'success',
                            time: 6000
                        });
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            closeModal() {
                this.showForgotPassword = false;
                this.forgotPasswordEmail = '';
            }
        },
        mounted() {
            const { returnUrl } = this.$route.query;
            if (returnUrl) {
                this.registerPath = `${this.registerPath}?returnUrl=${returnUrl}`;
            }

            const input = document.getElementById('email');
            if (input) {
                input.focus()
            }
        }
    };
</script>

<style lang="scss" scoped>
    .divider {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .spacer {
            flex: 1;
            height: 20px;
            border-top: 1px solid lightgrey;
        }

        .text {
            height: 40px;
        }
    }
</style>
